import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

// Share Availability flow dialog — contains BOTH steps internally:
//   • Step 1: event picker (shared <booking-event-picker> component)
//   • Step 2: invite compose form
//
// When opened with `preselectedEvent` set, starts at step 2 directly and hides
// the Back button (matches the scheduling-page event-row entry point, where
// the event is already known).
//
// Inputs (set by caller via dialogRef.componentInstance):
//   preselectedEvent  — optional event to skip step 1 and open step 2 directly
//   relatedRecord     — optional CRM record (Lead/Sub/Provider/Contact). When present,
//                       the compose form locks the To field to a chip for this record.
//   listAllEvents     — false (default) only the current user's events; true fetches all.
//                       Forwarded to the picker which now owns the fetch.
@Component({
  selector: 'share-availability-dialog',
  templateUrl: './share-availability-dialog.component.html',
  styleUrls: ['./share-availability-dialog.component.scss'],
  standalone: false
})
export class ShareAvailabilityDialogComponent implements OnInit {
  preselectedEvent: any = null;
  relatedRecord: any = null;
  listAllEvents: boolean = false;

  // Internal step state
  currentStep: 1 | 2 = 1;
  selectedEvent: any = null;
  // Tracks whether the user ever saw step 1 this session. Determines whether
  // the Back button shows on step 2 — when we launch straight into step 2 via
  // preselectedEvent, there's nothing to go back to.
  reachedStep2FromPicker: boolean = false;
  // Mirrors the picker's internal `creating` flag so step 1 footer disables Cancel
  // + Continue + close-X while a fast-create save is in flight.
  pickerCreating: boolean = false;


  onPickerCreatingChange(creating: boolean) {
    this.pickerCreating = creating;
  }


  constructor(
    private _dialogRef: MatDialogRef<ShareAvailabilityDialogComponent>
  ) {}


  ngOnInit() {
    // If a caller pre-selects the event, jump straight to step 2 — no picker needed.
    if (this.preselectedEvent?._id) {
      this.selectedEvent = this.preselectedEvent;
      this.currentStep = 2;
      this.reachedStep2FromPicker = false;
    }
  }


  // ── Header ───────────────────────────────────────────────


  getTitle(): string {
    if (this.currentStep === 2 && this.selectedEvent?.name) {
      return `Sharing: ${this.selectedEvent.name}`;
    }
    return 'Share availability';
  }


  getRecordDisplayName(): string {
    const r = this.relatedRecord;
    if (!r) return '';
    if (r?.name) return r.name;
    const first = (r?.first_name || r?.firstName || '').trim();
    const last = (r?.last_name || r?.lastName || '').trim();
    const composed = [first, last].filter(Boolean).join(' ');
    return composed || r?.email || '';
  }


  getRecordInitials(): string {
    const name = this.getRecordDisplayName();
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    const first = (parts[0] || '')[0] || '';
    const second = (parts[1] || '')[0] || '';
    return (first + second).toUpperCase();
  }


  // ── Step navigation ──────────────────────────────────────


  onEventSelected(event: any) {
    this.selectedEvent = event;
  }


  onContinueFromStep1() {
    if (!this.selectedEvent?._id) return;
    this.currentStep = 2;
    this.reachedStep2FromPicker = true;
  }


  onBackFromStep2() {
    this.currentStep = 1;
  }


  // ── Footer actions ───────────────────────────────────────


  cancel() {
    this._dialogRef.close(null);
  }


  // The compose form handles the actual /send HTTP call internally and emits
  // `sent` with the saved tracker on success. The dialog's only job now is to
  // close with that tracker so the caller (openShareAvailabilityFlow) resolves
  // with it. On failure the form stays open and shows its own error — the dialog
  // never sees a failure path here.
  onSent(savedTracker: any) {
    this._dialogRef.close(savedTracker);
  }
}